// =============================================================================
// ApXeroScreen — /ap/xero (T-331). Xero hand-off, master-detail. Coded invoices
// list on the left; click one and its Xero entry panel (copy fields + Bill/
// Spend commands) fills the right. Enter into Xero, tick it, move to the next.
// Nothing's deleted — "entered" greys into a strip with Undo. Budget + FTC ride
// as tracking; reference = {inv# or "Card sale"} | {budget code} | {project or OPEX}.
// =============================================================================

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, CheckCircle2, Copy, Check, RotateCcw, CreditCard, FileText, ExternalLink, Trash2, Send, AlertTriangle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Spinner, EmptyState } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import { XeroAccountPicker } from "./CodingCard";
import { xeroTransactionUrl } from "@/lib/xero";

const DEFAULT_ACCOUNT = "6000";
function csvCell(v: any): string { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; }

// Small inline copy button — for long fields (Reference) and the total.
function CopyMini({ text, title = "Copy" }: { text: string; title?: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" title={title}
      onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(text).then(() => { setOk(true); setTimeout(() => setOk(false), 1400); }, () => {}); }}
      className="ml-1.5 align-middle text-text-muted hover:text-accent inline-flex">
      {ok ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

// Duplicate key — same supplier + same rounded total. Used to flag look-alikes.
function dupKey(r: any): string {
  return `${String(r.supplier_name ?? "").toLowerCase().trim()}|${Math.round(Number(r.amount_inc_gst) || 0)}`;
}

export default function ApXeroScreen() {
  useRequireAuth();
  const [defaultAccount, setDefaultAccount] = useState(DEFAULT_ACCOUNT);
  const [entered, setEntered] = useState<any[]>([]);
  const [kindOverride, setKindOverride] = useState<Record<string, "bill" | "spend">>({});
  const [copied, setCopied] = useState(false);
  const [selId, setSelId] = useState<string | null>(null);
  const [xeroRef, setXeroRef] = useState("");

  const { data: codeMap = {} } = useQuery({
    queryKey: ["xero_budget_accounts"],
    queryFn: async () => {
      const { data } = await supabase.from("budget_codes").select("budget_code, xero_account_code").eq("is_active", true);
      const m: Record<string, string> = {};
      (data ?? []).forEach((r: any) => { if (r.xero_account_code) m[r.budget_code] = r.xero_account_code; });
      return m;
    },
  });

  // Card register — so a card-paid invoice shows WHICH card (last4) and which
  // Xero account to reconcile it against ("where it came from").
  const { data: cardMap = {} } = useQuery({
    queryKey: ["ap_card_register"],
    queryFn: async () => {
      const { data } = await supabase.from("card_register").select("card_id, last4, label, card_type, xero_account_name");
      const m: Record<string, any> = {};
      (data ?? []).forEach((c: any) => { m[c.card_id] = c; });
      return m;
    },
  });

  // T-488b — the real chart of accounts. This screen used to take a free-text
  // account, which is how "323 - Materials & Site Consumables" ended up stored
  // as a code and got the whole document rejected by Xero. Same picker as the
  // approval card now, so an invalid code cannot be entered here at all.
  const { data: xeroAccounts = [] } = useQuery({
    queryKey: ["xero_accounts_push"],
    queryFn: async () => {
      const { data } = await supabase.from("xero_accounts")
        .select("code, name, account_type, tax_code, description")
        .eq("active", true).eq("usable_for_ap", true).order("code");
      return (data ?? []) as any[];
    },
  });

  // T-353 — full active budget-code list so the code can be corrected inline
  // before pushing (e.g. TradeTools sockets mis-coded to 9020-COMMS).
  const { data: budgetCodeList = [] } = useQuery({
    queryKey: ["xero_budget_code_list"],
    queryFn: async () => {
      const { data } = await supabase.from("budget_codes")
        .select("budget_code, display_name").eq("is_active", true).order("sort_order");
      return (data ?? []) as any[];
    },
  });

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ["xero_handoff"],
    queryFn: async () => {
      const { data } = await supabase
        .from("ap_invoices")
        .select("ap_invoice_id, invoice_number, supplier_name, invoice_date, due_date, amount_ex_gst, gst_amount, amount_inc_gst, budget_code, ftc_class, payment_method, card_id, project_id, description, status, pdf_storage_path, xero_account_code, xero_tax_type, xero_bill_id, captured_by, captured_at, capture_source, captured_by_employee_id, is_reimbursement, reimburse_employee_id, xero_entry_type, approved_at")
        .in("status", ["captured", "approved"])
        // G4 — reimbursements are NOT pushed from here. They go out as one
        // bundled bill per person from /ap/reimbursements, so leaving them in
        // this queue too would give two buttons that both send the same receipt
        // to Xero — and paying a worker twice is not a recoverable mistake.
        .eq("is_reimbursement", false)
        // Oldest first — matches Xero entry order (James, Q4 cleanup).
        .order("invoice_date", { ascending: true, nullsFirst: false })
        .limit(200);
      const list = (data ?? []) as any[];
      const pids = Array.from(new Set(list.map((r) => r.project_id).filter(Boolean)));
      if (pids.length) {
        const { data: ps } = await supabase.from("projects").select("project_id, project_code").in("project_id", pids);
        const by = new Map((ps ?? []).map((p: any) => [p.project_id, p.project_code]));
        list.forEach((r) => { r._proj = by.get(r.project_id) ?? null; });
      }
      return list;
    },
  });

  const enteredIds = new Set(entered.map((e) => e.ap_invoice_id));

  // Filter + sort (James, Q4 cleanup): search anything (supplier, description,
  // ref, code, project, amount) + flip date order. Default oldest-first.
  const [listQ, setListQ] = useState("");
  const [oldestFirst, setOldestFirst] = useState(true);
  const list = useMemo(() => {
    let l = (rows as any[]).filter((r) => !enteredIds.has(r.ap_invoice_id));
    const q = listQ.trim().toLowerCase();
    if (q) {
      l = l.filter((r) =>
        [r.supplier_name, r.description, r.invoice_number, r.budget_code, r._proj,
         r.invoice_date, String(r.amount_inc_gst ?? ""), String(r.amount_ex_gst ?? "")]
          .some((v) => v && String(v).toLowerCase().includes(q)));
    }
    l = [...l].sort((a, b) => {
      const da = a.invoice_date ?? "9999", db2 = b.invoice_date ?? "9999";
      return oldestFirst ? da.localeCompare(db2) : db2.localeCompare(da);
    });
    return l;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, entered, listQ, oldestFirst]);

  // Flag suspected duplicates: same supplier + same total appearing twice.
  const dupCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of list) m.set(dupKey(r), (m.get(dupKey(r)) || 0) + 1);
    return m;
  }, [list]);
  const isDup = (r: any) => (dupCounts.get(dupKey(r)) || 0) > 1;
  const sel = list.find((r) => r.ap_invoice_id === selId) ?? list[0] ?? null;
  useEffect(() => { if (!selId && list[0]) setSelId(list[0].ap_invoice_id); }, [list, selId]);
  useEffect(() => { setXeroRef(""); }, [selId]);

  function kindOf(r: any): "bill" | "spend" {
    if (kindOverride[r.ap_invoice_id]) return kindOverride[r.ap_invoice_id];
    // T-442 — honour the Bill/Spend choice made at receipt approval; fall back to
    // payment method for older rows captured before that choice existed.
    if (r.xero_entry_type === "bill") return "bill";
    if (r.xero_entry_type === "spend_money") return "spend";
    return r.payment_method && String(r.payment_method).startsWith("card") ? "spend" : "bill";
  }
  function mapRow(r: any) {
    // Bookkeeper convention: {InvoiceNumber} | {BudgetCode} | {ProjectOrOPEX}
    // e.g. "01 | OX03_PREMISES | OPEX". Card receipts with no invoice number use
    // "Card sale" in the first slot; overhead (no project) shows "OPEX".
    const reference = [r.invoice_number || "Card sale", r.budget_code || null, r._proj || "OPEX"].filter(Boolean).join(" | ");
    const invNo = r.invoice_number || `AP-${String(r.ap_invoice_id).slice(0, 8)}`;
    // Manual override wins, else the budget code's mapped account, else default.
    const account = r.xero_account_code || (r.budget_code && codeMap[r.budget_code]) || defaultAccount;
    // T-485 — the tax treatment is now decided once, at approval, and stored.
    // Inferring it from "is there GST on the row" got GST-free bills wrong.
    const tax = r.xero_tax_type || (Number(r.gst_amount) > 0 ? "GST on Expenses" : "BAS Excluded");
    return { reference, invNo, account, tax };
  }
  function copyForXero(r: any) {
    const m = mapRow(r);
    const cd = r.card_id ? cardMap[r.card_id] : null;
    const block = [
      `Contact: ${r.supplier_name ?? ""}`, `Reference: ${m.reference}`, `Invoice #: ${m.invNo}`,
      `Date: ${r.invoice_date ?? ""}    Due: ${r.due_date ?? r.invoice_date ?? ""}`,
      `Ex GST: ${(Number(r.amount_ex_gst) || 0).toFixed(2)}    GST: ${(Number(r.gst_amount) || 0).toFixed(2)}    Total: ${(Number(r.amount_inc_gst) || 0).toFixed(2)}`,
      `Account: ${m.account}    Tax: ${m.tax}`, `Budget: ${r.budget_code ?? ""}${r.ftc_class ? `    FTC: ${r.ftc_class}` : ""}`,
      cd ? `Card: ****${cd.last4}${cd.xero_account_name ? ` -> ${cd.xero_account_name}` : ""}` : null,
      `Description: ${r.description ?? ""}`,
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(block).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }, () => {});
  }
  // T-488 — the actual push. Everything above this point on the screen exists
  // because we used to re-key invoices into Xero by hand; this replaces that.
  //
  // The Worker owns the rules, not this button: it re-runs the deterministic
  // blocker check server-side, posts as DRAFT, and refuses to send the same
  // invoice twice. So the button stays dumb on purpose — press, read the answer.
  const PUSH_BLOCKER_TEXT: Record<string, string> = {
    already_in_xero:            "Already in Xero.",
    not_approved:               "Not approved yet — approve it on Receipts first.",
    no_account_code:            "No Xero account code. Set one at approval or in the panel.",
    no_supplier:                "No supplier name.",
    zero_amount:                "Total is zero.",
    bill_without_due_date:      "It's a bill with no due date — Xero needs one.",
    totals_do_not_add_up:       "Ex GST + GST doesn't equal the total.",
    no_bank_account_configured: "This card has no Xero bank account mapped, so a spend can't be reconciled.",
    xero_not_connected:         "Xero isn't connected. Connect it on the AP home screen.",
  };
  // T-500c — Xero returns its refusal buried in a wall of echoed JSON, and the
  // one sentence that matters is at the very end. Pull the actual messages out
  // so the screen says what is wrong instead of making someone read a payload.
  function xeroReasons(detail: any): string[] {
    if (!detail) return [];
    let doc: any = detail;
    if (typeof detail === "string") {
      try { doc = JSON.parse(detail); } catch { return [detail.slice(0, 300)]; }
    }
    const out: string[] = [];
    for (const el of (doc?.Elements ?? []) as any[]) {
      for (const v of (el?.ValidationErrors ?? []) as any[]) {
        if (v?.Message) out.push(String(v.Message));
      }
      for (const li of (el?.LineItems ?? []) as any[]) {
        for (const v of (li?.ValidationErrors ?? []) as any[]) {
          if (v?.Message) out.push(`${li.Description ? `"${String(li.Description).slice(0, 40)}…" — ` : ""}${v.Message}`);
        }
      }
    }
    if (out.length === 0 && doc?.Message) out.push(String(doc.Message));
    return Array.from(new Set(out));
  }

  const [pushBusy, setPushBusy]   = useState(false);
  const [pushErr,  setPushErr]    = useState<string[] | null>(null);
  const [pushRaw,  setPushRaw]    = useState<string | null>(null);   // the full payload, on demand
  // Posted, but something after the post needs a human — currently the receipt.
  const [pushNote, setPushNote]   = useState<string | null>(null);
  // T-AP-XERO-NUM — bill number sent to Xero, editable per push. Some suppliers
  // (WorkCover) reuse the SAME number every year; Xero keys bills on
  // number+contact, so the renewal reads as an update to last year's PAID
  // bill and is refused. A year suffix here ("-2627") fixes it; the AP
  // record's own invoice number stays untouched.
  const [invNumEdit, setInvNumEdit] = useState("");
  // 9 Sep — the WorkCover $10k bill posted to Xero but the record-back failed
  // (approval trigger), and the blanket "Nothing was sent to Xero" line told
  // James the opposite of the truth. When the Worker says the transaction IS
  // in Xero, the card must say that, loudly.
  const [pushedButNotRecorded, setPushedButNotRecorded] = useState(false);
  useEffect(() => { setPushErr(null); setInvNumEdit(""); setPushedButNotRecorded(false); }, [selId]);

  async function pushToXero(r: any) {
    setPushBusy(true); setPushErr(null); setPushNote(null); setPushRaw(null);
    try {
      const override = invNumEdit.trim();
      const res  = await apiFetch("/ap/xero/push", {
        method: "POST",
        body:   JSON.stringify({
          ap_invoice_id: r.ap_invoice_id,
          // only when actually changed — absent means the Worker's old behaviour
          ...(override && override !== (r.invoice_number ?? "")
            ? { invoice_number_override: override } : {}),
        }),
      });
      const body = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        // 409 carries the blocker list; anything else is a single message.
        // The Worker now extracts these from the FULL response; the local parse
        // is only a fallback for an older Worker, since `detail` arrives truncated
        // and therefore usually will not parse.
        const fromXero: string[] = Array.isArray(body?.messages) && body.messages.length
          ? body.messages
          : (body?.error === "xero_rejected" ? xeroReasons(body?.detail) : []);
        const reasons: string[] = Array.isArray(body?.blockers) && body.blockers.length
          ? body.blockers.map((b: string) => PUSH_BLOCKER_TEXT[b] ?? b)
          : fromXero.length
            ? fromXero
            : [PUSH_BLOCKER_TEXT[body?.error] ?? body?.detail ?? body?.error ?? `Push failed (HTTP ${res.status}).`];
        setPushErr(reasons);
        setPushedButNotRecorded(body?.error === "posted_but_not_recorded");
        setPushRaw(body?.error === "xero_rejected" && typeof body?.detail === "string" ? body.detail : null);
        return;
      }
      // The transaction is in Xero. The receipt may not be — the upload runs
      // after the post and is deliberately not allowed to fail it, so say so
      // rather than letting a document-less bill look like a clean push.
      if (body?.attachment && body.attachment !== "ok") {
        setPushNote(body.attachment === "no_file"
          ? "Posted to Xero, but there was no receipt file on this record to attach."
          : `Posted to Xero, but the receipt didn't attach: ${body.attachment_detail ?? "unknown error"}. Attach it by hand in Xero.`);
      }
      // T-AP-GST-GUARD — the Worker predicts what Xero derives from the lines
      // it sent; a disagreement with the document's printed GST is said HERE,
      // while the coder is looking, not discovered at BAS time.
      if (body?.gst_mismatch) {
        setPushNote(
          `Pushed as a Draft, but the GST won't match: Xero will show $${Number(body.gst_xero_expected ?? 0).toFixed(2)}, ` +
          `the document says $${Number(body.gst_document ?? 0).toFixed(2)}. Fix the lines in Xero before Approve ` +
          `(insurance bills: premium + fees carry GST, stamp duty is BAS Excluded). A note is on the bill's history in Xero.`);
      }
      // The Worker has already set status + the Xero id on the row, so this only
      // moves it out of the queue on screen. Its number goes in the strip.
      setEntered((e) => [{ ...r, _xref: body?.xero_number ?? body?.xero_id ?? null, _pushed: true, _gstMismatch: !!body?.gst_mismatch }, ...e]);
      setSelId(null); setXeroRef(""); refetch();
    } catch (err: any) {
      setPushErr([err?.message === "not_authenticated" ? "Session expired — sign in again." : "Couldn't reach the API."]);
    } finally {
      setPushBusy(false);
    }
  }

  async function markEntered(r: any) {
    // A card Spend-money is already paid by definition — mark it Paid, not just
    // entered. A Bill goes to entered_in_xero (still sits in payables until paid).
    const paidNow = kindOf(r) === "spend";
    const patch: any = { status: paidNow ? "paid" : "entered_in_xero", xero_bill_number: xeroRef.trim() || null };
    if (paidNow) patch.paid_date = new Date().toISOString().slice(0, 10);
    await supabase.from("ap_invoices").update(patch).eq("ap_invoice_id", r.ap_invoice_id);
    setEntered((e) => [{ ...r, _xref: xeroRef.trim() }, ...e]); setSelId(null); setXeroRef(""); refetch();
  }
  // T-362 — for a Bill that's already paid at entry time (direct debit /
  // already reconciled in Xero): enter + mark paid in one go, no bounce to the
  // full queue. The ref field doubles as the payment reference.
  async function markEnteredPaid(r: any) {
    const ref = xeroRef.trim() || null;
    await supabase.from("ap_invoices").update({
      status:            "paid",
      xero_bill_number:  ref,
      payment_reference: ref,
      paid_date:         new Date().toISOString().slice(0, 10),
    }).eq("ap_invoice_id", r.ap_invoice_id);
    setEntered((e) => [{ ...r, _xref: ref }, ...e]); setSelId(null); setXeroRef(""); refetch();
  }
  // Fix the budget/tracking code inline before pushing. Updates ap_invoices;
  // the budget_spend trigger re-syncs the actual to the corrected code.
  async function updateBudgetCode(r: any, code: string) {
    await supabase.from("ap_invoices").update({ budget_code: code || null }).eq("ap_invoice_id", r.ap_invoice_id);
    refetch();
  }
  // T-498 — totals are edited here rather than only at approval, because a bad
  // GST is usually only discovered when Xero rejects the push. Whichever figure
  // is typed, GST is recomputed as total − ex so the row always reconciles; a
  // GST-free bill keeps GST at zero and ex equal to the total.
  const NO_GST = ["GST Free Expenses", "GST Free Income", "BAS Excluded"];
  async function saveTotals(r: any, newTotal: string | null, newEx: string | null) {
    const free  = NO_GST.includes(String(r.xero_tax_type ?? ""));
    const total = +(Number(newTotal ?? r.amount_inc_gst) || 0).toFixed(2);
    if (!total) return;
    let ex = +(Number(newEx ?? r.amount_ex_gst) || 0).toFixed(2);
    if (free) ex = total;
    // A blank or nonsense ex falls back to the standard 1/11 split.
    if (!ex || ex > total) ex = free ? total : +(total / 1.1).toFixed(2);
    let gst = free ? 0 : +(total - ex).toFixed(2);
    // T-500b — a supplied ex that is a cent or two off the ATO split poisons the
    // GST, because GST is total − ex. Xero computes 1/11 of the total and
    // rejects anything else. Snap silently when it is clearly a rounding
    // artifact; leave a bigger gap alone, because that is a real disagreement
    // with the document and should be looked at rather than papered over.
    if (!free) {
      const atoGst = +(total / 11).toFixed(2);
      if (Math.abs(gst - atoGst) <= 0.02) { gst = atoGst; ex = +(total - gst).toFixed(2); }
    }
    if (total === Number(r.amount_inc_gst) && ex === Number(r.amount_ex_gst) && gst === Number(r.gst_amount)) return;
    await supabase.from("ap_invoices")
      .update({ amount_inc_gst: total, amount_ex_gst: ex, gst_amount: gst })
      .eq("ap_invoice_id", r.ap_invoice_id);
    refetch();
  }

  // T-355 — inline edit of the Xero contact (supplier_name) + account override.
  // Persisted so what you push and what the ops record shows stay aligned.
  async function updateInvoiceField(r: any, patch: Record<string, any>) {
    await supabase.from("ap_invoices").update(patch).eq("ap_invoice_id", r.ap_invoice_id);
    refetch();
  }
  async function undo(r: any) {
    await supabase.from("ap_invoices").update({ status: "approved", xero_bill_number: null }).eq("ap_invoice_id", r.ap_invoice_id);
    setEntered((e) => e.filter((x) => x.ap_invoice_id !== r.ap_invoice_id)); refetch();
  }
  // Remove a duplicate / not-a-bill: set status='void'. The budget_spend sync
  // trigger deletes its actual on void, so it drops off this list AND out of the
  // project actuals — no double-count. Reversible (record isn't hard-deleted).
  async function removeInvoice(r: any) {
    if (!window.confirm(
      `Remove "${r.supplier_name ?? "this invoice"}" — ${fmtMoney(r.amount_inc_gst)}?\n\n` +
      `It's marked void: drops off this list and out of the project actuals. ` +
      `Nothing is hard-deleted — it can be restored later if needed.`
    )) return;
    await supabase.from("ap_invoices").update({ status: "void" }).eq("ap_invoice_id", r.ap_invoice_id);
    setSelId(null); refetch();
  }

  const csv = useMemo(() => {
    const header = ["*ContactName", "*InvoiceNumber", "Reference", "*InvoiceDate", "*DueDate", "Description", "*Quantity", "*UnitAmount", "*AccountCode", "*TaxType", "TrackingName1", "TrackingOption1", "TrackingName2", "TrackingOption2"];
    const lines = list.map((r) => { const m = mapRow(r); return [r.supplier_name ?? "", m.invNo, m.reference, r.invoice_date ?? "", r.due_date ?? r.invoice_date ?? "", r.description ?? r.budget_code ?? "", "1", (Number(r.amount_ex_gst) || 0).toFixed(2), m.account, m.tax, "Budget", r.budget_code ?? "", r.ftc_class ? "FTC" : "", r.ftc_class ?? ""].map(csvCell).join(","); });
    return [header.join(","), ...lines].join("\n");
  }, [list, codeMap, defaultAccount]);
  function downloadCsv() {
    const blob = new Blob([csv], { type: "text/csv" }); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `Xero_Bills_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // Shown next to the totals so a mismatch is visible before the push blocks it.
  const totalsAgree = !sel || Math.abs(
    (Number(sel.amount_ex_gst) || 0) + (Number(sel.gst_amount) || 0) - (Number(sel.amount_inc_gst) || 0)
  ) < 0.005;

  const m = sel ? mapRow(sel) : null;
  const kind = sel ? kindOf(sel) : "bill";
  const selCard = sel?.card_id ? cardMap[sel.card_id] : null;

  // Signed URL for the selected invoice's receipt file — so it can be viewed +
  // downloaded to upload into Xero and check the details. Scanned card receipts
  // keep their image on the pending_receipts row (the promote RPC never copied
  // it onto ap_invoices.pdf_storage_path), so fall back to that when needed.
  const { data: receipt, isFetching: receiptFetching } = useQuery({
    queryKey: ["ap_xero_receipt", sel?.ap_invoice_id],
    enabled: !!sel,
    queryFn: async () => {
      let path: string | null = sel.pdf_storage_path ?? null;
      if (!path) {
        const { data: pr } = await supabase
          .from("pending_receipts")
          .select("storage_path")
          .eq("promoted_to_ap_invoice_id", sel.ap_invoice_id)
          .order("promoted_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        path = (pr as any)?.storage_path ?? null;
      }
      if (!path) return null;
      const { data } = await supabase.storage.from("receipt-captures").createSignedUrl(path, 3600);
      return { url: data?.signedUrl ?? null, path };
    },
  });
  const receiptUrl = receipt?.url ?? null;

  // Provenance — who loaded it + reimbursement payee. James: MANDATORY on this
  // screen (bank rec with the bookkeeper), not just the receipts screen.
  const { data: prov } = useQuery({
    queryKey: ["ap_xero_prov", sel?.ap_invoice_id],
    enabled: !!sel,
    queryFn: async () => {
      // T-455 — prefer the authoritative loader (captured_by_employee_id).
      // Fall back to captured_by (users) for legacy / manually-created lines.
      const empId = sel.captured_by_employee_id ?? null;
      const [u, e] = await Promise.all([
        empId
          ? supabase.from("employees").select("first_name, last_name").eq("employee_id", empId).maybeSingle()
          : sel.captured_by
            ? supabase.from("users").select("login_email, employees:employee_id ( first_name, last_name )").eq("user_id", sel.captured_by).maybeSingle()
            : Promise.resolve({ data: null }),
        sel.reimburse_employee_id
          ? supabase.from("employees").select("first_name, last_name").eq("employee_id", sel.reimburse_employee_id).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      const uu: any = (u as any).data; const ee: any = (e as any).data;
      const loadedBy = uu
        ? (empId
            ? ([uu.first_name, uu.last_name].filter(Boolean).join(" ") || null)
            : ([uu.employees?.first_name, uu.employees?.last_name].filter(Boolean).join(" ") || uu.login_email || null))
        : null;
      return {
        loadedBy,
        reimbName: ee ? [ee.first_name, ee.last_name].filter(Boolean).join(" ") : null,
      };
    },
  });

  // Straight file download (no new tab) — pull the bytes as a blob so the
  // browser saves it instead of opening the cross-origin signed URL.
  const [dlBusy, setDlBusy] = useState(false);
  async function downloadReceipt() {
    if (!receipt?.path) return;
    setDlBusy(true);
    try {
      const { data, error } = await supabase.storage.from("receipt-captures").download(receipt.path);
      if (error || !data) { alert("Couldn't download the receipt — try View instead."); return; }
      const base = receipt.path.split("/").pop() || "receipt";
      const supplier = (sel.supplier_name || "receipt").replace(/[^\w]+/g, "_");
      const fname = base.includes(".") ? `${supplier}_${base}` : `${supplier}.${data.type.includes("pdf") ? "pdf" : "jpg"}`;
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } finally { setDlBusy(false); }
  }

  // ---------------------------------------------------------------------------
  // THE BACKLOG WARNING — 21 Aug 2026
  //
  // Bills to Pay used to show approved-but-not-yet-in-Xero bills, on the theory
  // (T-483) that hiding them was how bills went late. That put bills in a
  // payment queue that Xero had never heard of, and one got paid with nothing
  // behind it. Bills to Pay is now strictly what is in Xero.
  //
  // That protection still has to live somewhere, so it lives here, where the
  // backlog actually is. This screen is the only place an approved bill exists
  // between coding and Xero, so it is the screen that has to nag.
  // ---------------------------------------------------------------------------
  const stalled = useMemo(() => {
    const approved = rows.filter((r: any) => r.status === "approved");
    if (!approved.length) return null;
    const days = approved
      .map((r: any) => r.approved_at
        ? Math.floor((Date.now() - new Date(r.approved_at).getTime()) / 86400000)
        : null)
      .filter((n: number | null): n is number => n != null);
    return { count: approved.length, oldest: days.length ? Math.max(...days) : null };
  }, [rows]);

  return (
    <div className="p-4 md:p-6 max-w-[1500px] mx-auto">
      <Link to="/ap" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"><ArrowLeft size={14} /> AP / Spend</Link>
      {stalled && (
        <div className="mb-4 border border-warn/40 bg-warn/8 rounded-md p-3 flex items-start gap-2.5">
          <AlertTriangle size={16} className="text-warn mt-0.5 flex-shrink-0" />
          <div className="text-[13px] text-text">
            {stalled.count} approved {stalled.count === 1 ? "bill is" : "bills are"} waiting to go to Xero
            {stalled.oldest != null && stalled.oldest > 0 && <> — the oldest for {stalled.oldest} day{stalled.oldest === 1 ? "" : "s"}</>}.
            <div className="text-[12px] text-text-muted mt-0.5">
              They won't appear on Bills to pay until they're in Xero, so nothing can be paid
              against a bill Xero doesn't have. Send them across and they'll show up there ready to pay.
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Push to Xero</h1>
          <div className="text-text-muted text-[13px] mt-1">{list.length} ready. Pick one, copy it into Xero as a Bill or Spend-money, tick it off. Undo anytime — nothing's deleted.</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[12px] text-text-muted">Default acct
            <input value={defaultAccount} onChange={(e) => setDefaultAccount(e.target.value)} className="ml-1 w-16 bg-ink border border-line rounded px-2 py-1 text-[12px] text-text" />
          </label>
          <Button variant="secondary" icon={Download} onClick={downloadCsv} disabled={list.length === 0}>Bills CSV</Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : list.length === 0 && entered.length === 0 ? (
        <EmptyState icon={CheckCircle2} title="Nothing waiting for Xero" description="Coded invoices show here ready to push. Code some on the receipts screen first." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[440px_1fr] gap-5 items-start">
          {/* LEFT — list */}
          <div className="space-y-2 md:max-h-[80vh] md:overflow-y-auto pr-1">
            {/* Search + date order (oldest-first default = Xero entry order) */}
            <div className="flex items-center gap-2 sticky top-0 bg-bg z-10 pb-1">
              <input
                value={listQ}
                onChange={(e) => setListQ(e.target.value)}
                placeholder="Search supplier / desc / amount / code / date…"
                className="flex-1 bg-ink border border-line rounded px-2.5 py-1.5 text-[12px] text-text"
              />
              <button type="button" onClick={() => setOldestFirst(o => !o)}
                className="px-2.5 py-1.5 rounded border border-line text-[11px] text-text-muted hover:text-text whitespace-nowrap"
                title="Flip date order">
                {oldestFirst ? "Oldest first ↑" : "Newest first ↓"}
              </button>
            </div>
            {list.map((r) => {
              const on = sel && r.ap_invoice_id === sel.ap_invoice_id;
              return (
                <button key={r.ap_invoice_id} type="button" onClick={() => setSelId(r.ap_invoice_id)}
                  className={`w-full text-left px-4 py-3.5 rounded-lg border transition-colors ${on ? "bg-accent/10 border-accent/50" : isDup(r) ? "bg-danger/5 border-danger/40 hover:border-danger/60" : "bg-ink-card border-line hover:border-line-light"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[15px] text-text truncate">{r.supplier_name ?? "—"}</span>
                    <span className="text-[14px] text-text font-medium flex-shrink-0">{fmtMoney(r.amount_inc_gst ?? 0)}</span>
                  </div>
                  <div className="text-[11px] text-text-muted font-mono mt-1 flex items-center gap-1.5 flex-wrap">
                    {kindOf(r) === "spend" ? <CreditCard size={10} /> : <FileText size={10} />}
                    {r.budget_code ?? "uncoded"}{r._proj ? ` · ${r._proj}` : ""}{r.invoice_date ? ` · ${r.invoice_date}` : ""}
                    {r.card_id && cardMap[r.card_id] && <span className="text-text-dim">· ••••{cardMap[r.card_id].last4}</span>}
                    {r.is_reimbursement && <span className="text-warn font-medium">· REIMBURSE</span>}
                    {isDup(r) && <span className="text-danger">· possible dup</span>}
                  </div>
                </button>
              );
            })}
            {entered.length > 0 && (
              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-wider2 text-text-muted mb-1.5">Entered ({entered.length})</div>
                {entered.map((r) => (
                  <div key={r.ap_invoice_id} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-ink-raised/40 opacity-70 mb-1">
                    <CheckCircle2 size={12} className="text-ok flex-shrink-0" />
                    <span className="flex-1 min-w-0 text-[11px] text-text truncate">{r.supplier_name ?? "—"}</span>
                    {r._gstMismatch && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-danger/15 text-danger border border-danger/30 font-medium flex-shrink-0">
                        GST mismatch — fix in Xero
                      </span>
                    )}
                    <button type="button" onClick={() => undo(r)} className="text-[11px] text-accent hover:underline inline-flex items-center gap-0.5"><RotateCcw size={10} /> Undo</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT — detail / Xero commands */}
          {sel && m ? (
            <div className="bg-ink-card border border-line rounded-lg p-5 md:sticky md:top-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[16px] font-medium text-text">{sel.supplier_name ?? "—"}</div>
                  <div className="text-[12px] text-text-muted mt-0.5">{sel.description || sel.budget_code}</div>
                </div>
                <div className="text-[18px] font-semibold text-text flex-shrink-0 inline-flex items-center">
                  {fmtMoney(sel.amount_inc_gst ?? 0)}
                  <CopyMini text={(Number(sel.amount_inc_gst) || 0).toFixed(2)} title="Copy total" />
                </div>
              </div>

              {/* Provenance + routing — mandatory bookkeeper context (who loaded
                  it; reimbursement vs company spend) right where entry happens. */}
              <div className="mt-3 flex items-center gap-2 flex-wrap text-[12px]">
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-ink-raised border border-line">
                  <span className="text-text-muted">Loaded by</span>
                  <span className="text-text font-medium">{prov?.loadedBy ?? "unknown"}</span>
                  <span className="text-text-muted">
                    {sel.captured_at ? ` · ${fmtDate(sel.captured_at)}` : ""}{sel.capture_source ? ` (${sel.capture_source})` : ""}
                  </span>
                </span>
                {sel.is_reimbursement ? (
                  <span className="px-2 py-1 rounded bg-warn/15 text-warn border border-warn/40 font-medium">
                    REIMBURSEMENT — pays back {prov?.reimbName ?? "employee"} (bill to the person, not company spend)
                  </span>
                ) : kindOf(sel) === "bill" ? (
                  <span className="px-2 py-1 rounded bg-accent/10 text-accent border border-accent/30">
                    Company Bill{sel.due_date ? ` · due ${fmtDate(sel.due_date)}` : ""}
                  </span>
                ) : (
                  <span className="px-2 py-1 rounded bg-ok/10 text-ok border border-ok/30">
                    Company spend{sel.card_id && cardMap[sel.card_id] ? ` · card ••••${cardMap[sel.card_id].last4}` : ""}
                  </span>
                )}
              </div>

              {/* Receipt file — view + download to upload into Xero / check details */}
              {receiptUrl ? (
                <div className="mt-3 flex items-center gap-2.5 bg-ink-raised border border-line rounded-md px-3 py-2.5 flex-wrap">
                  <FileText size={16} className="text-accent flex-shrink-0" />
                  <span className="text-[12.5px] text-text font-medium flex-1 min-w-[90px]">Receipt attached</span>
                  <a
                    href={receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-line bg-ink text-[13px] font-medium text-text hover:border-accent hover:text-accent transition-colors"
                  >
                    <ExternalLink size={15} /> View
                  </a>
                  <button
                    type="button"
                    onClick={downloadReceipt}
                    disabled={dlBusy}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-accent text-white text-[13px] font-semibold hover:bg-accent-hover disabled:opacity-60 transition-colors shadow-sm"
                  >
                    <Download size={15} /> {dlBusy ? "Downloading…" : "Download"}
                  </button>
                </div>
              ) : receiptFetching ? (
                <div className="mt-3 text-[11px] text-text-muted">Loading receipt…</div>
              ) : (
                <div className="mt-3 text-[11px] text-text-dim">No receipt file on this entry.</div>
              )}

              {/* Card — where it came from / which Xero account to reconcile against */}
              {selCard && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-text bg-ink-raised border border-line rounded-md px-2.5 py-1 flex-wrap">
                  <CreditCard size={13} className="text-accent" />
                  <span className="font-mono">••••{selCard.last4}</span>
                  {selCard.label && <span className="text-text-muted">· {selCard.label}</span>}
                  {selCard.xero_account_name && <span className="text-text-muted">→ {selCard.xero_account_name}</span>}
                </div>
              )}
              {sel.payment_method && String(sel.payment_method).startsWith("card") && !selCard && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-warn bg-warn/10 border border-warn/40 rounded-md px-2.5 py-1">
                  <CreditCard size={13} /> Card-paid — card not identified. Assign it on the receipt to know where it came from.
                </div>
              )}

              {/* Possible duplicate */}
              {isDup(sel) && (
                <div className="mt-2 text-[12px] text-danger bg-danger/10 border border-danger/40 rounded-md px-2.5 py-1.5">
                  Possible duplicate — another invoice has the same supplier and total. Check before entering.
                </div>
              )}

              <div className="mt-3 inline-flex border border-line rounded-md bg-ink-raised p-0.5 gap-0.5">
                <button type="button" onClick={() => setKindOverride((o) => ({ ...o, [sel.ap_invoice_id]: "bill" }))} className={`px-3 py-1.5 rounded text-[12px] inline-flex items-center gap-1 ${kind === "bill" ? "bg-accent text-white" : "text-text-muted"}`}><FileText size={13} /> Bill</button>
                <button type="button" onClick={() => setKindOverride((o) => ({ ...o, [sel.ap_invoice_id]: "spend" }))} className={`px-3 py-1.5 rounded text-[12px] inline-flex items-center gap-1 ${kind === "spend" ? "bg-accent text-white" : "text-text-muted"}`}><CreditCard size={13} /> Spend money</button>
              </div>
              <div className="text-[12px] text-text-muted mt-2 leading-relaxed">
                {kind === "spend"
                  ? <>In Xero: <strong>New → Spend money</strong> on account <strong>{m.account}</strong>, reconcile against the card. Already paid, so no bill sits in payables.</>
                  : <>In Xero: <strong>New → Bill</strong>, Reference <strong>{m.reference || m.invNo}</strong>. If it's already been paid, mark it Paid straight away.</>}
              </div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-3.5 mt-5 text-[14px]">
                <div className="col-span-2"><div className="text-text-muted text-[10px] uppercase tracking-wider2">Description</div><div className="text-text break-words">{sel.description || "—"}{sel.description && <CopyMini text={sel.description} title="Copy description" />}</div></div>
                <div><div className="text-text-muted text-[10px] uppercase tracking-wider2">Contact — edit for Xero</div>
                  <input key={`contact-${sel.ap_invoice_id}`} defaultValue={sel.supplier_name ?? ""}
                    onBlur={(e) => { const v = e.target.value.trim(); if (v !== (sel.supplier_name ?? "")) updateInvoiceField(sel, { supplier_name: v || null }); }}
                    className="mt-0.5 w-full bg-ink border border-line rounded px-2 py-1 text-[13px] text-text" />
                </div>
                <div><div className="text-text-muted text-[10px] uppercase tracking-wider2">Reference</div><div className="text-text font-mono break-words">{m.reference || "—"}{m.reference && <CopyMini text={m.reference} title="Copy reference" />}</div></div>
                <div><div className="text-text-muted text-[10px] uppercase tracking-wider2">Bill # sent to Xero — edit if the supplier reuses numbers</div>
                  <input
                    key={`invnum-${sel.ap_invoice_id}`}
                    value={invNumEdit !== "" ? invNumEdit : (sel.invoice_number ?? "")}
                    onChange={(e) => setInvNumEdit(e.target.value)}
                    placeholder="Bill number in Xero"
                    className="mt-0.5 w-full bg-ink border border-line rounded px-2 py-1 text-[13px] text-text font-mono"
                    title='Annual renewals (WorkCover, insurers) often reuse the same number every year — Xero then thinks this is last year&apos;s bill. Add a suffix like "-2627" and push. The reference still carries the real number.'
                  />
                </div>
                <div><div className="text-text-muted text-[10px] uppercase tracking-wider2">Xero account
                  {!sel.xero_account_code && !codeMap[sel.budget_code] && <span className="text-warn"> *default</span>}
                  {!!m.account && !(xeroAccounts as any[]).some((a: any) => a.code === m.account) && (
                    <span className="text-danger"> * not a valid code — pick one</span>
                  )}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <div className="flex-1">
                      <XeroAccountPicker
                        key={`acct-${sel.ap_invoice_id}`}
                        value={(xeroAccounts as any[]).some((a: any) => a.code === m.account) ? m.account : ""}
                        accounts={xeroAccounts as any[]}
                        onChange={(code) => { if (code && code !== sel.xero_account_code) updateInvoiceField(sel, { xero_account_code: code }); }}
                      />
                    </div>
                    <CopyMini text={m.account} title="Copy account" />
                  </div>
                </div>
                <div><div className="text-text-muted text-[10px] uppercase tracking-wider2">Tax</div><div className="text-text font-mono">{m.tax}</div></div>
                <div><div className="text-text-muted text-[10px] uppercase tracking-wider2">Budget (tracking) — edit to recode</div>
                  <select value={sel.budget_code ?? ""} onChange={(e) => updateBudgetCode(sel, e.target.value)}
                    className="mt-0.5 w-full bg-ink border border-line rounded px-2 py-1 text-[13px] text-text font-mono">
                    <option value="">— none —</option>
                    {sel.budget_code && !(budgetCodeList as any[]).some((b) => b.budget_code === sel.budget_code) && (
                      <option value={sel.budget_code}>{sel.budget_code} (current)</option>
                    )}
                    {(budgetCodeList as any[]).map((b) => (
                      <option key={b.budget_code} value={b.budget_code}>{b.budget_code} — {b.display_name}</option>
                    ))}
                  </select>
                </div>
                <div><div className="text-text-muted text-[10px] uppercase tracking-wider2">FTC (tracking)</div><div className="text-text font-mono">{sel.ftc_class ?? "—"}</div></div>
                {/* T-498 — editable, because a wrong GST used to mean delete the
                    invoice and re-upload it. Type the total and the ex-GST from
                    the document; GST is always the difference, so the three can
                    never disagree and Xero cannot reject the split. */}
                <div className="col-span-2">
                  <div className="text-text-muted text-[10px] uppercase tracking-wider2">
                    Totals — edit to match the invoice
                    {!totalsAgree && <span className="text-danger"> · doesn't add up</span>}
                  </div>
                  <div className="mt-1 grid grid-cols-3 gap-2">
                    <label className="block">
                      <span className="text-[10px] text-text-dim">Total inc GST</span>
                      <input key={`tot-${sel.ap_invoice_id}`} defaultValue={(Number(sel.amount_inc_gst) || 0).toFixed(2)}
                        inputMode="decimal"
                        onBlur={(e) => saveTotals(sel, e.target.value, null)}
                        className="w-full bg-ink border border-line rounded px-2 py-1 text-[13px] text-text font-mono" />
                    </label>
                    <label className="block">
                      <span className="text-[10px] text-text-dim">Ex GST</span>
                      <input key={`ex-${sel.ap_invoice_id}`} defaultValue={(Number(sel.amount_ex_gst) || 0).toFixed(2)}
                        inputMode="decimal"
                        onBlur={(e) => saveTotals(sel, null, e.target.value)}
                        className="w-full bg-ink border border-line rounded px-2 py-1 text-[13px] text-text font-mono" />
                    </label>
                    <div>
                      <span className="text-[10px] text-text-dim">GST (derived)</span>
                      <div className="w-full bg-ink-raised border border-line rounded px-2 py-1 text-[13px] text-text-muted font-mono">
                        {(Number(sel.gst_amount) || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
                <div><div className="text-text-muted text-[10px] uppercase tracking-wider2">Invoice date</div><div className="text-text font-mono">{sel.invoice_date ?? "—"}</div></div>
                <div><div className="text-text-muted text-[10px] uppercase tracking-wider2">Due date</div><div className="text-text font-mono">{sel.due_date ?? "—"}</div></div>
              </div>

              <div className="mt-6 pt-4 border-t border-line flex items-center justify-end gap-2 flex-wrap">
                <input value={xeroRef} onChange={(e) => setXeroRef(e.target.value)} placeholder="Xero bill # / ID (optional)"
                  className="flex-1 min-w-[160px] bg-ink border border-line rounded px-3 py-2 text-[13px] text-text" />
                <Button variant="danger" icon={Trash2} onClick={() => removeInvoice(sel)} title="Remove this invoice (void — drops off the list + actuals, reversible)">Remove</Button>
                {/* T-488d — after a push this opens the transaction itself, not
                    the list it is somewhere inside. */}
                <Button variant="secondary" icon={ExternalLink}
                  onClick={() => {
                    const url = xeroTransactionUrl(sel.xero_entry_type, sel.xero_bill_id)
                      ?? (kind === "bill" ? "https://go.xero.com/app/bills" : "https://go.xero.com/app/bank-accounts");
                    window.open(url, "_blank", "noopener");
                  }}>
                  {sel.xero_bill_id ? "View in Xero" : "Open Xero"}
                </Button>
                <Button variant="secondary" icon={copied ? Check : Copy} onClick={() => copyForXero(sel)}>{copied ? "Copied" : "Copy"}</Button>
                {/* T-362 — a bill can be entered (awaiting payment) OR entered &
                    paid in one go (direct debit / already reconciled). Spend money
                    is paid by definition so it only shows the one button. */}
                {kind === "bill" && (
                  <Button variant="secondary" icon={CheckCircle2} onClick={() => markEnteredPaid(sel)} title="Enter in Xero AND mark paid now (already paid / direct debit). Ref field = payment reference.">
                    Entered &amp; paid
                  </Button>
                )}
                {/* T-488 — "Entered" is now the manual fallback, not the main path.
                    Push does the work: creates the DRAFT in Xero and marks the row.
                    Both stay, because a bill occasionally has to be keyed by hand. */}
                <Button variant="secondary" icon={CheckCircle2} onClick={() => markEntered(sel)}
                  title="I keyed this into Xero myself — just mark it done here.">
                  {kind === "spend" ? "Entered (paid)" : "Entered"}
                </Button>
                <Button icon={Send} disabled={pushBusy} onClick={() => pushToXero(sel)}
                  title={kind === "spend"
                    ? "Create this as a DRAFT bank transaction in Xero"
                    : "Create this as a DRAFT bill in Xero"}>
                  {pushBusy ? "Pushing…" : "Push to Xero"}
                </Button>
              </div>

              {/* Why it wouldn't go. These are the Worker's own checks, so what
                  shows here is exactly what stopped it — not a guess from the UI. */}
              {pushNote && (
                <div className="mt-3 flex items-start gap-2 rounded border border-warn/30 bg-warn/10 px-3 py-2 text-[12px] text-warn">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <div><div className="font-medium">In Xero — receipt not attached</div>
                    <div className="mt-0.5">{pushNote}</div></div>
                </div>
              )}
              {pushErr && (
                <div className="mt-3 flex items-start gap-2 rounded border border-warn/30 bg-warn/10 px-3 py-2 text-[12px] text-warn">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">Not pushed{pushErr.length > 1 ? ` — ${pushErr.length} things to fix` : ""}</div>
                    <ul className="mt-1 space-y-0.5">
                      {pushErr.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                    {pushedButNotRecorded ? (
                      <div className="mt-1.5 rounded border border-danger/40 bg-danger/10 px-2 py-1.5 text-danger font-medium">
                        The bill IS in Xero — do NOT push again. Find it in Xero, put its number in the
                        box below and click "Entered" to record it here.
                      </div>
                    ) : (
                      <div className="mt-1.5 text-text-muted">Nothing was sent to Xero.</div>
                    )}
                    {pushErr.some((t) => /payments or credit notes allocated/i.test(t)) && (
                      <div className="mt-1.5 rounded border border-line bg-ink px-2 py-1.5 text-text-muted">
                        This usually means a bill with this NUMBER already exists in Xero and is paid —
                        annual renewals (WorkCover, insurers) reuse the same number every year. Edit
                        "Bill # sent to Xero" above (add a suffix like <span className="font-mono">-2627</span>) and push again.
                      </div>
                    )}
                    {pushRaw && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-[11px] text-text-dim hover:text-text-muted">
                          Xero's full response
                        </summary>
                        <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all text-[10px] text-text-dim">
                          {pushRaw}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-ink-card border border-line rounded-lg p-8 text-center text-text-muted text-[13px]">Pick an invoice on the left to copy into Xero.</div>
          )}
        </div>
      )}
    </div>
  );
}
